import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { OrderTableRow } from "src/services/datatable/datatable.interface";
import { OrderRowClickEvent } from "src/services/datatable/datatable.types";
import { NavigationService } from "src/services/navigation/navigation.service";
import { PlanUniqueId } from "../../../../../services/subscription-type/subscription-type.interface";
import { tryParseDate } from "src/services/utils/timestamp.utils";
import { ContextmenuType } from "@swimlane/ngx-datatable";
import {
  ApiOrder,
  ApiOrderStatus,
  GetOrdersQueryStringArgs,
  OrderApiService,
} from "../../../../../services/order/order-api.service";
import {
  defaultPageSize,
  initialPage,
  NgxDatablePageInfo,
  NgxDatatablePage,
  NgxDatatableSortInfo,
} from "../ngx-datatable.const";
import { LoadingService } from "../../../../../services/loading/loading.service";
import { MessageService } from "../../../../../services/message/message.service";
import { camelCaseToDisplayString, displayStringToCamelCase } from "../../../../../services/utils/string.utils";
import { OrderStatus } from "../../../../../services/order/order-status.type";

export type OrderTableRightClickEvent = { content: OrderTableRow; event: MouseEvent; type: ContextmenuType };

export enum OrderTableColumn {
  base = "base",
  createdAt = "createdAt",
  customer = "customerFullName",
  driver = "driverFullName",
  earliestFulfillment = "earliestFulfillment",
  formattedAddress = "formattedAddress",
  service = "service",
  status = "status",
  subscription = "firestoreSubscriptionType",
}

export const orderTableColumnsInDisplayOrder: OrderTableColumn[] = [
  OrderTableColumn.base,
  OrderTableColumn.createdAt,
  OrderTableColumn.customer,
  OrderTableColumn.driver,
  OrderTableColumn.earliestFulfillment,
  OrderTableColumn.formattedAddress,
  OrderTableColumn.subscription,
  OrderTableColumn.service,
  OrderTableColumn.status,
];

export type OrderTableColumnVisible = {
  [column in OrderTableColumn]: {
    sortable: boolean;
    visible: boolean;
  };
};

@Component({
  selector: "rc-order-table",
  templateUrl: "./order-table.component.html",
  styleUrls: ["./order-table.component.scss"],
  standalone: false,
})
export class OrderTableComponent implements OnChanges {
  public readonly PlanUniqueId = PlanUniqueId;
  @Input() public columnConfig: OrderTableColumnVisible;
  @Input() public defaultSortBy: NonNullable<GetOrdersQueryStringArgs>["sortBy"] | undefined;
  @Input() public driverId: string | undefined;
  @Input() public forcedSortBy: NonNullable<GetOrdersQueryStringArgs>["sortBy"] | undefined;
  @Input() public itemId: string | undefined;
  @Input() public latestFulfillmentGt: Date | undefined;
  public loadFailed = false;
  @Output() public onTableContextMenu = new EventEmitter<OrderTableRightClickEvent>();
  public page: NgxDatatablePage<ApiOrder> = initialPage;
  @Input() public searchString: string | undefined;
  @Input() public showMobileLayout: boolean;
  @Input() public status: OrderStatus | undefined;
  public readonly tryParseDate = tryParseDate;
  @Input() public userId: string | undefined;
  protected readonly camelCaseToDisplayString = camelCaseToDisplayString;

  constructor(
    private readonly loadingService: LoadingService,
    private readonly messageService: MessageService,
    private readonly navigationService: NavigationService,
    private readonly orderApiService: OrderApiService,
  ) {}

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const onlySearchStringChanged = changes["searchString"] && Object.keys(changes).length === 1;

    if (onlySearchStringChanged) {
      await this.updatePage({ count: 0, limit: defaultPageSize, offset: 0, pageNumber: 0, pageSize: 0 }, undefined);
    } else {
      await this.loadingService.wrapInLoading(async () => {
        await this.updatePage({ count: 0, limit: defaultPageSize, offset: 0, pageNumber: 0, pageSize: 0 }, undefined);
      });
    }
  }

  public async setPage(pageInfo: NgxDatablePageInfo): Promise<void> {
    await this.loadingService.wrapInLoading(async () => {
      await this.updatePage(pageInfo, undefined);
    });
  }

  public async setSort(sortInfo: NgxDatatableSortInfo): Promise<void> {
    await this.loadingService.wrapInLoading(async () => {
      await this.updatePage(
        { count: 0, limit: defaultPageSize, offset: 0, pageNumber: 0, pageSize: defaultPageSize },
        sortInfo,
      );
      this.page.sortInfo = sortInfo;
    });
  }

  public getCellClass(event: { row: OrderTableRow }) {
    return { "completed-row": event.row.status === "completed" };
  }

  public async goToOrder(event: OrderRowClickEvent) {
    if (event.type !== "click") {
      return;
    }

    await this.navigationService.goToOrderDetailsPage(event.row.id);
  }

  public async updatePage(pageInfo: NgxDatablePageInfo, sortInfo: NgxDatatableSortInfo | undefined): Promise<void> {
    pageInfo ??= this.page.pageInfo;
    sortInfo ??= this.page.sortInfo;

    const args: GetOrdersQueryStringArgs = {
      first: defaultPageSize.toString(),
      offset: (pageInfo.offset * defaultPageSize).toString(),
    };

    if (this.status) {
      args.status = displayStringToCamelCase(this.status) as ApiOrderStatus;
    }

    if (this.driverId) {
      args.driverExternalId = this.driverId;
    }

    if (this.userId) {
      args.userId = this.userId;
    }

    if (this.searchString) {
      args.fuzzySearchQuery = this.searchString;
    }

    if (this.latestFulfillmentGt) {
      args.latestFulfillmentGt = this.latestFulfillmentGt.toISOString();
    }

    if (this.itemId) {
      args.externalItemId = this.itemId;
    }

    if (this.forcedSortBy) {
      args.sortBy = this.forcedSortBy;
    } else if (sortInfo) {
      args.sortBy = `${sortInfo.sorts[0].prop}${camelCaseToDisplayString(sortInfo.sorts[0].dir)}`;
    } else if (this.defaultSortBy) {
      args.sortBy = this.defaultSortBy;
    }

    try {
      const response = await this.orderApiService.getOrders(args);
      this.page.pageInfo = {
        ...pageInfo,
        count: response.totalCount,
        limit: defaultPageSize,
        pageSize: defaultPageSize,
      };
      this.page.data = response.orders;
      this.loadFailed = false;
    } catch (error) {
      this.page.data = [];
      this.loadFailed = true;
      await this.messageService.presentUnexpectedErrorToast("Could not load orders", error);
    }
  }
}
